/**
 * Local UI state that is NOT game state: board orientation, sound, the
 * breakpoint, the measured layout, and which ply the MoveList is browsing.
 *
 * It lives in a store, not in React state, for two reasons:
 *  1. the Stage leaves take no props and read no context (UX spec §6a), so
 *     "flip the board" cannot be a prop coming down from the screen root;
 *  2. `viewport` and `boardSize` must follow the CONTENT BOX, not the window.
 *     The app header (~118 px) and the icon drawer (~108 px) are outside the
 *     screen, and the design lab renders the body inside its own frame — a
 *     window-derived size is wrong in both cases. `Body` measures itself with
 *     `onLayout` and calls `setLayout`.
 */
import {create} from 'zustand';

import {getPrefSync, setPref} from '@/Store/liveGame/platform/storage';

import {LobbyVariant} from '../pages/home/resolveLobbyVariant';

export type V2Viewport = 'desktop' | 'phone';

/**
 * The mute toggle is STICKY (P2): a player who turned the cues off does not
 * want them back on the next game. Read through `platform/storage`'s
 * synchronous half for the same reason `useV2Theme` does — `rail/useSounds`
 * subscribes on mount and an async read would let one cue through first.
 * '1' | '0', default ON (the web lane has no mute today, so sound-on is the
 * behaviour every current user already has).
 */
const SOUND_KEY = 'play_v2_sound';

const readStoredSound = (): boolean => getPrefSync(SOUND_KEY) !== '0';

const writeStoredSound = (soundOn: boolean): void => {
  setPref(SOUND_KEY, soundOn ? '1' : '0');
};

/**
 * Which tab the RAIL is on (fix D / R3; 'details' added by fix G, 2026-10-06).
 * 'moves' and 'info' only appear while a game (or a watch) owns the board; the
 * tournament panels only appear in tournament mode (`rail/Rail/model.ts`'s
 * `railMode`) — which tabs exist for which combination is `rail/tabs/
 * tabSpec.ts`'s `tabsFor`, the ONE place that decides it.
 *
 * Fix G REMOVED the old `tournamentTab` / `V2TournamentTab` / `TOURNAMENT_TAB_FOR`
 * / `RAIL_TAB_FOR` mirror: that was the full-width tournament PAGE's own tab
 * state, kept in step with this one because the two surfaces used to take the
 * board away from each other. The page is gone — the tournament IS the rail
 * now — so there is nothing left to mirror into. The one caller that read an
 * OLD component's own tab-name string (`rail/tournament/usePanelContext.ts`'s
 * `setActiveTab`, handed to `standings.tsx` / `games.tsx` as a prop) keeps a
 * small local map of its own, next to its only use.
 */
export type V2RailTab =
  | 'moves'
  | 'info'
  | 'details'
  | 'standings'
  | 'pairings'
  | 'leaderboard';

/**
 * Every fixed dimension of the desktop Stage + Rail row, in one place so the
 * sizing formula and the StyleSheets cannot drift apart.
 */
export const LAYOUT = {
  /**
   * One PlayerRow card. 56 since the clean-up of 2026-10-05 (R6.3): a 36 px
   * avatar and a 40 px clock pill over 8 px of padding, where M1a's 60 carried a
   * 44 px avatar and a 44 px clock face. The row and the sizing formula read the
   * SAME number, so the board grows by the 8 px the two rows gave back.
   */
  playerRow: 56,
  /** page padding, left and right */
  pagePadding: 24,
  /**
   * Page padding ABOVE and BELOW the Stage row. 12, half the horizontal one,
   * since the clean-up of 2026-10-05 (owner item 2): the board is height-bound
   * on every normal window, so every vertical pixel spent on page margin came
   * straight off the board.
   */
  pagePaddingVertical: 12,
  /** between the Stage column and the Rail */
  stageRailGap: 24,
  /** between the three Stage children (there are two of them) */
  stageGap: 4,
  /** the board frame's padding, left and right */
  boardFramePadding: 12,
  /** the board frame's padding above and below the board (owner item 2) */
  boardFrameVerticalPadding: 8,
  /** the board frame's padding on the phone */
  boardFramePaddingPhone: 8,
  railPreferred: 400,
  railMin: 360,
  railMax: 440,
  /**
   * The WIDE rail (fix D / R1 + §T4): tournament mode or self-analysis. A
   * standings table and a three-line engine readout do not fit 400 px, and both
   * of them are states the player opts into — so the rail takes 80 px more and
   * the board gives them up, instead of every casual game paying for it.
   */
  railWidePreferred: 480,
  railWideMin: 400,
  railWideMax: 540,
  /** the board never goes below this, even on a very short window */
  boardMin: 320,
} as const;

export type V2UiStore = {
  /**
   * Which home screen this mount resolved to (M2 spec §1). Decided ONCE by
   * `pages/home/index.tsx` and pushed here so a unit can read the arm it is in
   * (copy, analytics) without a prop being drilled through the page.
   */
  lobbyVariant: LobbyVariant | null;
  flipped: boolean;
  soundOn: boolean;
  viewport: V2Viewport;
  boardSize: number;
  railWidth: number;
  /** the measured content box (excludes the app header and the drawer) */
  contentWidth: number;
  contentHeight: number;
  /**
   * null = following the live position. A number = the user is browsing the
   * notation and the board shows the position after that many plies.
   */
  viewPly: number | null;
  /**
   * The RAIL's tab (fix D / R3). Reset to 'moves' whenever a game spawns
   * (`rail/Rail/model.ts`), so a pairing never lands on a standings table.
   */
  railTab: V2RailTab;
  /**
   * The rail is in its WIDE mode: tournament panels or self-analysis are on
   * (`rail/Rail/model.ts`). It feeds `computeRailWidth`, so a toggle re-sizes
   * the rail AND the board in one write.
   */
  railWide: boolean;
  /**
   * The watched game has ENDED and the 1.5 s modal delay has elapsed (old
   * L3407-3409 on the app side; this repo's watch-end callback has the same
   * delay). `hooks/useTournamentEffects` owns the timer; the result overlay
   * reads this so the spectator card cannot flash the instant the end lands.
   */
  watchResultReady: boolean;
  /**
   * The result overlay's close (x). The ended position and the move list stay
   * on screen for browsing; only the card goes away. Reset when the next game
   * is seeded (see `LiveBody`).
   */
  resultDismissed: boolean;
  /**
   * The app SHELL's chrome (the `<Header>` atom on the desktop, the Drawer's
   * own header on mobile web) is hidden while this screen is mounted —
   * decision D8, "the whole Play screen", lobby included. `chrome/useChromeHidden`
   * raises it on mount and lowers it on unmount, and `navigators/Application`
   * reads it; the OLD lane never raises it, so a lane flip restores the header.
   * The exit control the hidden header takes away is `chrome/PlayHeader`.
   */
  chromeHidden: boolean;
  setLobbyVariant: (lobbyVariant: LobbyVariant) => void;
  setFlipped: (flipped: boolean) => void;
  setSoundOn: (soundOn: boolean) => void;
  setViewport: (viewport: V2Viewport) => void;
  setBoardSize: (boardSize: number) => void;
  setViewPly: (viewPly: number | null) => void;
  setRailTab: (railTab: V2RailTab) => void;
  /** wide rail on/off; re-derives `railWidth` and `boardSize` in one write */
  setRailMode: (railWide: boolean) => void;
  setWatchResultReady: (watchResultReady: boolean) => void;
  setResultDismissed: (resultDismissed: boolean) => void;
  setChromeHidden: (chromeHidden: boolean) => void;
  /** one write per measured layout change: viewport + rail width + board size */
  setLayout: (contentWidth: number, contentHeight: number) => void;
};

/** The breakpoint the old screen used (`useMediaQuery`'s isMobileOrTab). */
export const viewportFor = (contentWidth: number): V2Viewport =>
  contentWidth <= 768 ? 'phone' : 'desktop';

/**
 * The Rail is a FIXED column so the board can take the rest: 400 px, clamped
 * into [360, 440]. On a narrow desktop the rail gives way first — it never
 * takes more than a third of the row, because the board has to dominate.
 *
 * `wide` is the tournament / analysis mode of fix D: the same formula over
 * [400, 540] preferring 480. The share rule is unchanged, so a narrow desktop
 * still clamps down to the mode's own minimum rather than eating the board.
 */
export const computeRailWidth = (
  contentWidth: number,
  wide: boolean = false,
): number => {
  const preferred = wide ? LAYOUT.railWidePreferred : LAYOUT.railPreferred;
  const min = wide ? LAYOUT.railWideMin : LAYOUT.railMin;
  const max = wide ? LAYOUT.railWideMax : LAYOUT.railMax;
  const room = contentWidth - 2 * LAYOUT.pagePadding - LAYOUT.stageRailGap;
  if (room <= 0) {
    return preferred;
  }
  const byShare = Math.round(room / 3);
  return Math.max(min, Math.min(max, Math.min(preferred, byShare)));
};

/**
 * The board is the largest square that fits the Stage column:
 *
 *   availableH = contentH − 2×playerRow − 2×pagePaddingVertical − 2×stageGap
 *                         − 2×boardFrameVerticalPadding
 *   availableW = contentW − railW − stageRailGap − 2×pagePadding
 *                         − 2×boardFramePadding
 *   boardSize  = min(availableH, availableW)
 *
 * The frame terms are the board frame's own padding: this formula sizes the
 * BOARD, and the frame wraps it, so without them the column overflows by exactly
 * that much and the bottom player row is clipped. They differ per axis since the
 * clean-up of 2026-10-05 (8 above/below, 12 left/right).
 *
 * There is deliberately NO upper clamp any more (the old one was 600): on a
 * 1512×982 window with the real chrome this yields ~658 px instead of ~520.
 */
export const computeDesktopBoardSize = (
  contentWidth: number,
  contentHeight: number,
  railWidth: number = computeRailWidth(contentWidth),
): number => {
  const availableH =
    contentHeight -
    2 * LAYOUT.playerRow -
    2 * LAYOUT.pagePaddingVertical -
    2 * LAYOUT.stageGap -
    2 * LAYOUT.boardFrameVerticalPadding;
  const availableW =
    contentWidth -
    railWidth -
    LAYOUT.stageRailGap -
    2 * LAYOUT.pagePadding -
    2 * LAYOUT.boardFramePadding;
  return Math.max(
    LAYOUT.boardMin,
    Math.floor(Math.min(availableH, availableW)),
  );
};

export const computePhoneBoardSize = (contentWidth: number): number =>
  Math.max(LAYOUT.boardMin, Math.min(contentWidth, 420) - 24);

export const computeBoardSize = (
  viewport: V2Viewport,
  contentWidth: number,
  contentHeight: number,
  wide: boolean = false,
): number =>
  viewport === 'phone'
    ? computePhoneBoardSize(contentWidth)
    : computeDesktopBoardSize(
        contentWidth,
        contentHeight,
        computeRailWidth(contentWidth, wide),
      );

/**
 * The geometry a (contentWidth, contentHeight, railWide) triple implies. One
 * place, so `setLayout` and `setRailMode` cannot derive it differently.
 */
const geometryFor = (
  contentWidth: number,
  contentHeight: number,
  wide: boolean,
): {viewport: V2Viewport; railWidth: number; boardSize: number} => {
  const viewport = viewportFor(contentWidth);
  return {
    viewport,
    railWidth:
      viewport === 'phone'
        ? contentWidth
        : computeRailWidth(contentWidth, wide),
    boardSize: computeBoardSize(viewport, contentWidth, contentHeight, wide),
  };
};

export const useV2Ui = create<V2UiStore>(set => ({
  lobbyVariant: null,
  flipped: false,
  soundOn: readStoredSound(),
  viewport: 'desktop',
  boardSize: 480,
  railWidth: LAYOUT.railPreferred,
  contentWidth: 0,
  contentHeight: 0,
  viewPly: null,
  railTab: 'moves',
  railWide: false,
  watchResultReady: false,
  resultDismissed: false,
  chromeHidden: false,
  setLobbyVariant: lobbyVariant =>
    set(s => (s.lobbyVariant === lobbyVariant ? s : {...s, lobbyVariant})),
  setFlipped: flipped => set({flipped}),
  setSoundOn: soundOn => {
    writeStoredSound(soundOn);
    set({soundOn});
  },
  setViewport: viewport =>
    set(s => (s.viewport === viewport ? s : {...s, viewport})),
  setBoardSize: boardSize =>
    set(s => (s.boardSize === boardSize ? s : {...s, boardSize})),
  setViewPly: viewPly => set({viewPly}),
  setRailTab: railTab =>
    set(s => (s.railTab === railTab ? s : {...s, railTab})),
  setRailMode: railWide =>
    set(s => {
      if (s.railWide === railWide) {
        return s;
      }
      if (s.contentWidth <= 0 || s.contentHeight <= 0) {
        return {...s, railWide};
      }
      return {
        ...s,
        railWide,
        ...geometryFor(s.contentWidth, s.contentHeight, railWide),
      };
    }),
  setWatchResultReady: watchResultReady =>
    set(s =>
      s.watchResultReady === watchResultReady ? s : {...s, watchResultReady},
    ),
  setResultDismissed: resultDismissed => set({resultDismissed}),
  setChromeHidden: chromeHidden => set({chromeHidden}),
  setLayout: (contentWidth, contentHeight) =>
    set(s => {
      if (contentWidth <= 0 || contentHeight <= 0) {
        return s;
      }
      const {viewport, railWidth, boardSize} = geometryFor(
        contentWidth,
        contentHeight,
        s.railWide,
      );
      if (
        s.contentWidth === contentWidth &&
        s.contentHeight === contentHeight &&
        s.viewport === viewport &&
        s.railWidth === railWidth &&
        s.boardSize === boardSize
      ) {
        return s;
      }
      return {
        ...s,
        contentWidth,
        contentHeight,
        viewport,
        railWidth,
        boardSize,
      };
    }),
}));

export default useV2Ui;
